// Types
import type { Ref, ComputedRef, ShallowRef } from 'vue';
import type {
  IUnsavedChangesGuardOptions,
  IUnsavedChangesGuard,
} from './types';
import { EConfirmationAction } from './types';

export const useUnsavedChangesGuard = <TState>(
  options: IUnsavedChangesGuardOptions<TState>
): IUnsavedChangesGuard<TState> => {
  const { extractCurrentState, onSave } = options;

  const baselineState: ShallowRef<TState | null> = shallowRef<TState | null>(null);
  const showConfirmation: Ref<boolean> = ref<boolean>(false);
  const renderKey: Ref<number> = ref<number>(0);
  const pendingAction: Ref<(() => void) | null> = ref<(() => void) | null>(null);

  const isDirty: ComputedRef<boolean> = computed<boolean>(() => {
    if (baselineState.value === null) return false;

    const currentState: TState = extractCurrentState();

    return JSON.stringify(currentState) !== JSON.stringify(baselineState.value);
  });

  const setBaselineState = (state: TState): void => {
    baselineState.value = state;
  };

  const setShowConfirmation = (value: boolean): void => {
    showConfirmation.value = value;
  };

  const setRenderKey = (value: number): void => {
    renderKey.value = value;
  };

  const setPendingAction = (action: (() => void) | null): void => {
    pendingAction.value = action;
  };

  const snapshotState = (): void => {
    setBaselineState(extractCurrentState());
  };

  const resetComponents = (): void => {
    setRenderKey(renderKey.value + 1);
  };

  const interceptChange = <T>(newValue: T, applyChange: (value: T) => void): boolean => {
    if (isDirty.value && baselineState.value !== null) {
      setPendingAction(() => applyChange(newValue));
      resetComponents();
      setShowConfirmation(true);

      return true;
    }

    applyChange(newValue);

    return false;
  };

  const handleConfirmation = async (
    action: EConfirmationAction,
    onProceed: () => void
  ): Promise<void> => {
    setShowConfirmation(false);

    if (action === EConfirmationAction.CANCEL) {
      setPendingAction(null);
      resetComponents();

      return;
    }

    if (action === EConfirmationAction.SAVE_AND_PROCEED) {
      const saveSuccess: boolean = await onSave();

      if (!saveSuccess) {
        setPendingAction(null);
        resetComponents();

        return;
      }
    }

    if (pendingAction.value) {
      pendingAction.value();
      setPendingAction(null);
    }

    onProceed();
  };

  return {
    baselineState,
    isDirty,
    showConfirmation,
    renderKey,
    snapshotState,
    interceptChange,
    handleConfirmation,
    resetComponents,
  };
};
